import { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import type { PaginationOptions } from "swiper/types";
import right from "@/assets/images/right.svg";
import { useEnableAds } from "@/hooks/useAds";
import { AdsBitmedia } from "../ads/ads-bitmedia";
import { useBanner } from "./data";

// Constants
const PAGINATION_CONFIG: PaginationOptions = {
  clickable: true,
  renderBullet: (_, className) => `<div class="${className}"></div>`,
  bulletActiveClass: "!bg-white",
  bulletClass: "w-4 h-1 rounded-full bg-white/10",
  horizontalClass: "custom-pagination",
};

const ViewAllSlide = memo(() => {
  const { t } = useTranslation();

  return (
    <Link className="contents" to={"./campaign"}>
      <div className="flex items-center justify-center gap-1 h-[112px] flex-shrink-0 w-full rounded-xl overflow-hidden border border-[#FEC537] border-dashed">
        <p className="text-base text-view">{t("View all campaign")}</p>
        <img src={right} alt="right" width={24} height={24} loading="lazy" />
      </div>
    </Link>
  );
});

const AdsenseSlide = memo(() => (
  <div className="flex items-center justify-center gap-1 h-[112px] flex-shrink-0 w-full rounded-xl overflow-hidden relative">
    <div className="absolute h-full w-full flex items-center justify-center">
      <div
        className="h-[112px] w-full overflow-hidden"
        style={{
          background: "#121212",
        }}
      >
        <AdsBitmedia />
      </div>
    </div>
  </div>
));

function shuffleFirstThreeItems<T>(list: T[]): T[] {
  const firstThree = list.slice(0, 3);
  // Fisher-Yates shuffle cho 3 item đầu tiên
  for (let i = firstThree.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [firstThree[i], firstThree[j]] = [firstThree[j], firstThree[i]];
  }
  // Ghép phần đầu đã shuffle với phần còn lại
  return [...firstThree, ...list.slice(3)];
}

export function BannerSlider() {
  const { i18n } = useTranslation();
  const { data } = useBanner(i18n.language);
  const enableAds = useEnableAds();

  const list = useMemo(() => {
    if (!data?.sliderImages || data.sliderImages.length === 0) {
      return [];
    }

    const temp = data.sliderImages.sort((a, b) => a.orderId - b.orderId);
    return shuffleFirstThreeItems(temp);
  }, [data?.sliderImages]);

  const listView = useMemo(() => {
    if (!list || list.length === 0) {
      return [];
    }

    const l = list.map((slide) => {
      return {
        key: `banner-id-${slide.id}`,
        element: (
          <Link
            className="contents"
            to={slide.navigateTo}
            target={slide.navigateTo.startsWith("http") ? "_blank" : "_self"}
            data-action={slide.id ? `banner-id-${slide.id}` : undefined}
          >
            <div className="h-[112px] flex-shrink-0 w-full rounded-xl overflow-hidden">
              <img
                src={slide.imageUrl}
                alt="world-future"
                className="w-full h-full object-fill"
              />
            </div>
          </Link>
        ),
      };
    });

    l.push({
      key: "all-campaign",
      element: <ViewAllSlide />,
    });

    if (enableAds) {
      l.push({
        key: "adsense",
        element: <AdsenseSlide />,
      });
    }

    return l;
  }, [enableAds, list]);

  if (listView.length === 0) {
    return null;
  }

  return (
    <div className="px-4 py-3">
      <Swiper
        spaceBetween={30}
        centeredSlides={true}
        autoplay={{
          delay: 5000,
          disableOnInteraction: false,
        }}
        key={i18n.language}
        pagination={PAGINATION_CONFIG}
        modules={[Autoplay, Pagination, Navigation]}
        className="mySwiper !overflow-visible"
      >
        {listView.map((slide) => (
          <SwiperSlide key={slide.key}>{slide.element}</SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}
